import React from "react"

import { renderTestID } from "../helpers/renderTestID"
import { GridDiagramProps } from "./GridDiagram"
import { GridDiagramCellInfo } from "./GridDiagramCellInfo"
import { BaseGridDiagram, BaseGridDiagramCell } from "./BaseGridDiagram"

export interface SelectableGridDiagramProps extends GridDiagramProps {
  /**
   * Optional validator callback, which will be invoked when doing form validation.
   */
  validator?: (selectedSlots: string[]) => boolean
}

interface SelectableGridDiagramState {
  /**
   * The list of the slots selected.
   */
  selectedCells: string[]
}

/**
 * Renders a grid of cells which can be selected as input similar to a checkbox.
 */
export class SelectableGridDiagram extends React.PureComponent<
  SelectableGridDiagramProps,
  SelectableGridDiagramState> {

  private readonly inputRef = React.createRef<HTMLInputElement>()

  public constructor(props: SelectableGridDiagramProps) {
    super(props)

    this.state = {
      selectedCells: []
    }
  }

  public componentDidMount() {
    this.attachHooks()
  }

  public componentWillUnmount() {
    this.detachHooks()
  }

  public getCellSelection(): string[] {
    return this.state.selectedCells
        .concat(
            this.props.grid.flatMap((row) => row.cells)
                .filter((cell) => cell.selected)
                .map((cell) => cell.identifier)
        )
  }

  public attachHooks() {
    // used to check if form is valid on form submit stage
    const input = this.inputRef.current
    if (input) {
      // @ts-ignore
      input.$validate = () => {
        return this.props.validator ? this.props.validator(this.getCellSelection()) : true
      }

      // @ts-ignore
      input.$reset = () => {
        this.setState(() => ({ selectedCells: [] }))
      }
    }
  }

  public detachHooks() {
    // clear hooks to prevent resource leak
    const input = this.inputRef.current
    if (input) {
      // @ts-ignore
      input.$validate = undefined
      // @ts-ignore
      input.$reset = undefined
    }
  }

  public handleSelect(identifier: string) {
    const selectedCells = this.state.selectedCells

    if (selectedCells.includes(identifier)) {
      this.setState((state) =>
        ({ selectedCells: state.selectedCells.filter((cell) => cell !== identifier) }))
    } else {
      this.setState((state) =>
        ({ selectedCells: state.selectedCells.concat(identifier) }))
    }
  }

  private buildCell = (cell: GridDiagramCellInfo): BaseGridDiagramCell => {
    const selectable = cell.enabled
    const selected =
      cell.selected
      || this.state.selectedCells.includes(cell.identifier)
    const empty = !cell.enabled && !cell.selected

    const modelCell = {
      ...cell,
      color: empty ? "light-gray" : (selected ? cell.color : "gray"),
      enabled: selectable,
      selected,
      size: this.props.cellSize,
      onSelect: this.handleSelect.bind(this, cell.identifier)
    }

    return (modelCell)
  }

  public render() {
    // Bind context before JSX for performance reasons
    const buildCell = this.buildCell.bind(this)

    return (
      <div className="grid-diagram" data-testid={renderTestID(this.props.testID)}>
        <BaseGridDiagram
          {...this.props}
          buildCell={buildCell}
        />

        <input
          ref={this.inputRef}
          type="hidden"
          name="selected_slots$json"
          value={JSON.stringify(this.getCellSelection())}
        />
      </div>
    )
  }
}
